"use client";

import React, { useState, useRef } from "react";
import { useHomepageContext } from "../context/HomepageContext";
import { PiImage, PiUpload, PiX } from "react-icons/pi";
import logger from "@/utils/logger";

interface EditableImageProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    alt?: string;
    placeholder?: string;
    width?: number;
    height?: number;
}

export default function EditableImage({
    value,
    onChange,
    className = "",
    alt = "Image",
    placeholder = "Click to upload image...",
    width = 400,
    height = 300,
}: EditableImageProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [urlValue, setUrlValue] = useState(value);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const context = useHomepageContext();
    const readOnly = context ? context.readOnly : false;

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        try {
            // Create a local preview URL for immediate display
            const localUrl = URL.createObjectURL(file);
            onChange(localUrl);

            // In a real app, you would upload to a server here
            // For demo purposes, we'll keep the local URL
            // const formData = new FormData();
            // formData.append('file', file);
            // const response = await fetch('/api/upload', { method: 'POST', body: formData });
            // const data = await response.json();
            // onChange(data.url);
        } catch (error) {
            logger.error("Upload failed:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleUrlSubmit = () => {
        if (urlValue.trim()) {
            onChange(urlValue.trim());
            setShowUrlInput(false);
        }
    };

    if (readOnly) {
        return value ? (
            <img
                src={value}
                alt={alt}
                className={className}
                style={{ width, height: '100%', objectFit: 'cover' }}
            />
        ) : (
            <div
                className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className}`}
                style={{ width, height }}
            >
                <PiImage className="w-8 h-8" />
            </div>
        );
    }

    return (
        <div className="relative group">
            {value ? (
                <div className="relative">
                    <img
                        src={value}
                        alt={alt}
                        className={`${className} transition-all duration-300 group-hover:opacity-90`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                            title="Upload new image"
                        >
                            <PiUpload className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                            onClick={() => {
                                setUrlValue("");
                                onChange("");
                            }}
                            className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                            title="Remove image"
                        >
                            <PiX className="w-5 h-5 text-red-500" />
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    className={`relative flex flex-col items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all ${className}`}
                    style={{ width: '100%', height }}
                    onClick={() => setShowUrlInput(true)}
                >
                    <PiImage className="w-10 h-10 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">{placeholder}</span>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="p-1.5 bg-white rounded-md shadow-sm hover:bg-gray-50">
                            <PiUpload className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
            />

            {/* URL Input Modal */}
            {showUrlInput && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Add Image URL</h3>
                            <button
                                onClick={() => setShowUrlInput(false)}
                                className="p-1 hover:bg-gray-100 rounded-full"
                            >
                                <PiX className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Image URL
                                </label>
                                <input
                                    type="url"
                                    value={urlValue}
                                    onChange={(e) => setUrlValue(e.target.value)}
                                    placeholder="https://example.com/image.jpg"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                />
                            </div>
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                                <span>or</span>
                                <button
                                    onClick={() => {
                                        setShowUrlInput(false);
                                        fileInputRef.current?.click();
                                    }}
                                    className="text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    Upload from computer
                                </button>
                            </div>
                            {urlValue && (
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => setShowUrlInput(false)}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUrlSubmit}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Add Image
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-white flex flex-col items-center">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mb-2" />
                        <span className="text-sm">Uploading...</span>
                    </div>
                </div>
            )}
        </div>
    );
}
